<template>
  <div class="product__button-group" id="product-button-group">
    <template v-if="productStore.product">
      <template v-if="hasOnlyUSTag && !isUSALocation">
        <!-- Add a hidden add to cart button here because customily need to find a non-disabled button to load -->
        <sib-button class="hidden"> Add To Cart </sib-button>
        <sib-button disabled> Selling in the U.S.A. only </sib-button>
      </template>
      <template
        v-else-if="
          isUSALocation ||
          (shouldLoadProductDescriptionAndShippingByVariantType &&
            !productStore.isSelectedVariantOnlySellInUSA) ||
          (!shouldLoadProductDescriptionAndShippingByVariantType &&
            !productStore.isProductOnlySellInUSA)
        "
      >
        <template v-if="productStore.product.availableForSale">
          <template v-if="isYourDesignProductType">
            <sib-button
              role="primary"
              @click="emit('submitYourRequest')"
              :class="buyNowButtonClass"
            >
              SUBMIT REQUEST
            </sib-button>
          </template>
          <template v-else>
            <template v-if="isQrGreetingCardProductType">
              <!-- Add a hidden add to cart button here because customily need to find a non-disabled button to load -->
              <sib-button class="hidden"> Add To Cart </sib-button>
              <sib-button
                :disabled="checkingUrl"
                :class="addToCartButtonClass"
                :loading="loadingAddToCartQrGreetingCard"
                @click="clickAddToCartButton"
                role="primary"
              >
                Save and Continue to Customize Your Digital QR Content
              </sib-button>
            </template>

            <sib-button
              v-else
              @click="emit('addToCart')"
              :class="addToCartButtonClass"
              role="primary"
            >
              Add To Cart
            </sib-button>
            <!-- <sib-button
              role="primary"
              @click="emit('buyNow')"
              :class="buyNowButtonClass"
            >
              BUY NOW
            </sib-button> -->
          </template>
        </template>
        <sib-button v-else type="default" disabled> Sold out</sib-button>
      </template>
      <template v-else>
        <!-- Add a hidden add to cart button here because customily need to find a non-disabled button to load -->
        <sib-button class="hidden"> Add To Cart </sib-button>
        <sib-button disabled> Selling in the U.S.A. only </sib-button>
      </template>
    </template>

    <sib-skeleton-loading
      v-if="!productStore.product || loadingCustomily"
      height="3em"
    ></sib-skeleton-loading>
  </div>

  <p v-if="isCALocation" style="color: #eb001b">
    <strong>Notice:</strong> Please be advised that due to the Canada Post
    strike, orders to Canada may be delayed or canceled.
  </p>
  <div
    v-if="showNoteQrGreetingCardFlow"
    class="note-qr-greeting-card-flow mt-sm"
  >
    Oops! It looks like you missed a step. To add the QR Greeting Card & Digital
    Content to your cart, please make sure you complete Step 1 by customizing
    your QR greeting card.
    <sib-link
      :to="'/collections/personalized-interactive-greeting-cards-with-surprise-digital-content'"
      title="Personalized Greeting Cards with Surprise Digital Content"
    >
      Click here</sib-link
    >
    to choose a QR greeting card and start customizing it.
  </div>

  <client-only
    v-if="
      deviceStore.isPhone &&
      (productStore.isUILandingProduct || productStore.isProductUINewVersion)
    "
  >
    <Teleport to="body">
      <div
        class="container sticky-button-add-to-cart"
        id="sticky-button-add-to-cart"
      >
        <sib-button
          type="button"
          role="primary"
          size="md"
          width="full"
          @click="openProductOptionsPopup"
        >
          Add To Cart
        </sib-button>
      </div>
    </Teleport>
    <ProductOptionsPopup
      v-if="deviceStore.isPhone && productStore.isUILandingProduct"
    ></ProductOptionsPopup>
  </client-only>
</template>

<script setup lang="ts">
import EventBus from '@/services/eventbus'
import LocationService, { UserLocation } from '@/services/location'
import useProductStore from '@/store/product'
import {
  EVENT_CUSTOMILY_DONE_LOADING,
  EVENT_CUSTOMILY_START_LOADING,
  US_COUNTRY_CODE,
  PRODUCT_TYPE_YOUR_DESIGN,
  PRODUCT_TYPE_QR_GREETING_CARD,
  EVENT_CHECKING_URL,
  EVENT_ADD_TO_CART_DIGITAL_CONTENT_PRODUCT_FALSE,
  debounce,
  EVENT_SHOW_PRODUCT_OPTIONS_POPUP,
  PRODUCT_TAG_SELL_ONLY_US,
  CA_COUNTRY_CODE,
} from '@/utils'
import { isProductHasType, isProductHasTags } from '@/utils/product'
import { onMounted, ref, watch, onUnmounted, computed } from 'vue'
import ProductOptionsPopup from './ProductOptionsPopup.vue'
import useDeviceStore from '@/store/device'
import useRequestLocationStore from '@/store/requestLocation'

const requiredClasses = ['ld-over-inverse']
const loadingClasses = ['running']
const pointerDisabledClasses = ['disable-pointer-events']
const addToCartButtonClass = 'product__add-to-cart-button'
const buyNowButtonClass = 'button-buy-now'
const loadingCustomily = ref(false)
const maximumTimeToWaitCustomilyLoading = 6000 // 6 seconds in miliseconds
const props = withDefaults(
  defineProps<{
    loadingAddToCart: boolean
  }>(),
  {
    loadingAddToCart: false,
  }
)

const emit = defineEmits<{
  (e: 'addToCart'): void
  (e: 'buyNow'): void
  (e: 'submitYourRequest'): void
}>()

const requestLocation = useRequestLocationStore()
const deviceStore = useDeviceStore()
const productStore = useProductStore()
let timeout: any = 0
const checkingUrl = ref(false)
const loadingAddToCartQrGreetingCard = ref(false)

const shouldLoadProductDescriptionAndShippingByVariantType = computed(
  () => productStore.shouldLoadProductDescriptionAndShippingByVariantType
)
const isYourDesignProductType = computed(() => {
  return isProductHasType(productStore?.product, [PRODUCT_TYPE_YOUR_DESIGN])
})

const isQrGreetingCardProductType = computed(() => {
  return isProductHasType(productStore?.product, [
    PRODUCT_TYPE_QR_GREETING_CARD,
  ])
})

const isCALocation = computed(
  () =>
    requestLocation.countryCode &&
    requestLocation.countryCode.toUpperCase() == CA_COUNTRY_CODE
)

const hasOnlyUSTag = computed(() =>
  isProductHasTags(productStore?.product, [PRODUCT_TAG_SELL_ONLY_US])
)

const isUSALocation = ref(true)
const showNoteQrGreetingCardFlow = ref(false)

EventBus.on(EVENT_CUSTOMILY_START_LOADING, onCustomilyStartLoading)
EventBus.on(EVENT_CUSTOMILY_DONE_LOADING, onCustomilyDoneLoading)
EventBus.on(EVENT_ADD_TO_CART_DIGITAL_CONTENT_PRODUCT_FALSE, () => {
  showNoteQrGreetingCardFlow.value = true
})

const debounceScroll = debounce(() => {
  handleStickyAddToCartButton()
}, 50)

onMounted(() => {
  // Toggle class by js because customily overide our buttons.
  // If we use Vue method it will clear customily modifidation
  const addToCartSelector = `.${addToCartButtonClass}`
  const buyNowSelector = `.${buyNowButtonClass}`
  watch(
    () => props.loadingAddToCart,
    (value) => {
      if (value) {
        loadingAddToCartQrGreetingCard.value = true
      }
      toggleClasses(addToCartSelector, buyNowSelector, value)
    }
  )
  LocationService.onLocationReady((location: UserLocation | null) => {
    if (location) {
      isUSALocation.value =
        location.country_code.toLowerCase() == US_COUNTRY_CODE.toLowerCase()
    }
  })

  EventBus.on(EVENT_CHECKING_URL, (value) => {
    checkingUrl.value = value
  })

  if (
    deviceStore.isPhone &&
    (productStore.isUILandingProduct || productStore.isProductUINewVersion)
  ) {
    window.addEventListener('scroll', debounceScroll, {
      passive: true,
    })
  }
})

onUnmounted(() => {
  EventBus.off(EVENT_CUSTOMILY_START_LOADING, onCustomilyStartLoading)
  EventBus.off(EVENT_CUSTOMILY_DONE_LOADING, onCustomilyDoneLoading)
  clearTimeout(timeout)

  if (deviceStore.isPhone && productStore.isUILandingProduct)
    window.removeEventListener('scroll', debounceScroll)
})

function clickAddToCartButton() {
  if (checkingUrl.value == true) return

  emit('addToCart')
  loadingAddToCartQrGreetingCard.value = true
  setTimeout(() => {
    loadingAddToCartQrGreetingCard.value = false
  }, 2000)
}

function onCustomilyStartLoading() {
  loadingCustomily.value = true
  timeout = setTimeout(() => {
    loadingCustomily.value = false
  }, maximumTimeToWaitCustomilyLoading)
}

function onCustomilyDoneLoading() {
  loadingCustomily.value = false
  clearTimeout(timeout)
}

function toggleClasses(
  addToCartSelector: string,
  buyNowSelector: string,
  isLoading: boolean
) {
  const buttons = document.querySelectorAll(
    `${addToCartSelector}, ${buyNowSelector}`
  )
  if (isLoading) {
    const addedClassList = [
      ...requiredClasses,
      ...loadingClasses,
      ...pointerDisabledClasses,
    ]
    buttons.forEach((element) => {
      element.classList.add(...addedClassList)
    })
  } else {
    const removedClassList = [...loadingClasses, ...pointerDisabledClasses]
    buttons.forEach((element) => {
      element.classList.remove(...removedClassList)
    })
  }
}

function isButtonVisible(button: any) {
  const rect = button.getBoundingClientRect()
  return rect.top >= 0
}

function handleStickyAddToCartButton() {
  const productButtonGroup = document.querySelector('#product-button-group')
  const stickyButtonAddToCart = document.querySelector(
    '#sticky-button-add-to-cart'
  )
  if (!productButtonGroup || !stickyButtonAddToCart) return
  if (!isButtonVisible(productButtonGroup)) {
    stickyButtonAddToCart.classList.add('sticky-button-add-to-cart--fixed')
  } else {
    stickyButtonAddToCart.classList.remove('sticky-button-add-to-cart--fixed')
  }
}

function openProductOptionsPopup() {
  if (productStore.isUILandingProduct) {
    EventBus.trigger(EVENT_SHOW_PRODUCT_OPTIONS_POPUP)
  } else {
    const buttonGroup = document.getElementById('product-button-group')
    if (!buttonGroup) return
    window.scrollTo({ top: buttonGroup.offsetTop - 400, behavior: 'smooth' })
  }
}
</script>

<style lang="scss">
.product__button-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  position: relative;
  min-height: 3em;
  .sib-ske-loading {
    position: absolute;
    top: 0;
    left: 0;
    background-color: #fff;
  }

  @include media-lg-up {
    flex-direction: row;

    .sib-button {
      max-width: 250px;
    }
  }
}
.sib-button {
  &.ld-over-inverse {
    &.disable-pointer-events {
      &::before {
        display: none !important;
      }
      .sib-button__content {
        display: none !important;
      }
      .sib-button__loading {
        display: inline-block !important;
      }
      .ld-cycle.ld-ring {
        display: none !important;
      }
    }
  }
}

.note-qr-greeting-card-flow {
  display: block;
  color: var(--color-secondary);
  a {
    color: var(--color-primary);
  }
}

.sticky-button-add-to-cart {
  display: unset;
  position: fixed;
  width: 100%;
  left: 0;
  bottom: 0;
  z-index: var(--promotion-bar-z-index);
  transform: translateY(200%);
  transition: transform var(--animation-duration);
  &--fixed {
    transform: translateY(0);
    display: block;
  }
}
</style>
